import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Wifi, WifiOff, Maximize, Volume2, VolumeX } from "lucide-react";
import { useAuction, useRemaining } from "@/hooks/useAuction";
import { Logo, StatusPill, PlayerAvatar, TeamBadge } from "@/components/opl/Common";
import { useAuctionFx, SpinWheel, Stamp, Sponsors } from "@/components/opl/AuctionFx";
import { api, fmtL } from "@/lib/api";

export function useClubs() {
  const [clubs, setClubs] = useState(null);
  useEffect(() => { api.get("/clubs").then((r) => setClubs(r.data)); }, []);
  return clubs;
}

function ClubPicker({ base }) {
  const clubs = useClubs();
  return (
    <div className="min-h-screen grid place-items-center p-6 stage-bg" data-testid="club-picker">
      <div className="text-center space-y-6">
        <Logo size="lg" />
        <p className="eyebrow">Choose auction</p>
        <div className="flex flex-wrap gap-4 justify-center">
          {(clubs || []).map((c) => <Link key={c.id} to={`${base}/${c.id}`} data-testid={`pick-club-${c.name}`} className="glass-navy gold-frame rounded-3xl px-14 py-10 text-3xl font-bold tracking-tight hover:-translate-y-1 hover:shadow-[0_0_40px_rgba(245,208,97,.3)] transition-[transform,box-shadow] duration-300"><span className="block text-xs uppercase tracking-[0.3em] text-[#F5D061] mb-2">Club auction</span>{c.name}</Link>)}
        </div>
      </div>
    </div>
  );
}

export function TimerBar({ snap, offset, big }) {
  const rem = useRemaining(snap?.state?.ends_at, offset);
  if (!snap?.config?.bid_timer_seconds) return null;
  const total = snap.config.bid_timer_seconds;
  const pct = rem == null ? 0 : Math.min(100, (rem / total) * 100);
  const danger = rem != null && rem <= (snap?.config?.going_twice_at || 5);
  return (
    <div data-testid="countdown-timer">
      <div className={`num font-black ${big ? "text-6xl" : "text-3xl"} ${danger ? "text-red-400" : "text-white"}`}>
        {rem == null ? "00" : String(Math.ceil(rem)).padStart(2, "0")}<span className="text-base text-slate-500 ml-1">s</span>
      </div>
      <div className="h-2 mt-3 rounded-full bg-white/5 overflow-hidden">
        <div className={`h-full transition-[width] duration-200 ${danger ? "bg-red-500" : "gold-bg"}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function PlayerPanel({ p, status }) {
  if (!p) return (
    <div className="glass-navy gold-frame rounded-3xl p-8 h-full grid place-items-center text-center" data-testid="live-no-player">
      <div><img src="/logo-white.png" alt="Oneiros Sports Club" className="mx-auto w-56 max-w-full opacity-90 drop-shadow-[0_0_30px_rgba(245,208,97,.35)]" /><p className="mt-6 text-sm uppercase tracking-[0.3em] text-[#F5D061]">Awaiting nomination</p></div>
    </div>
  );
  const stats = Object.entries(p.stats || {}).filter(([, v]) => v !== "" && v != null).slice(0, 6);
  return (
    <div key={p.id} className="glass-navy gold-frame rounded-3xl p-6 h-full flex flex-col rise relative overflow-hidden" data-testid="live-player-panel">
      <div className="absolute -top-24 left-1/2 -translate-x-1/2 h-56 w-56 rounded-full bg-[#D4AF37]/20 blur-3xl pointer-events-none" />
      <Stamp status={status} />
      {p.lot_no != null && <div className="absolute top-4 left-4 num font-bold text-2xl px-3 py-1 rounded-lg gold-bg shadow-[0_0_20px_rgba(245,208,97,.35)]" data-testid="live-player-number">#{p.lot_no}</div>}
      <div className="relative mx-auto">
        <div className="absolute -inset-3 rounded-3xl bg-gradient-to-br from-[#FFE082]/40 via-[#D4AF37]/10 to-[#996515]/40 blur-xl" />
        <PlayerAvatar player={p} size={210} className="relative border-2 border-[#F5D061]/70 !rounded-3xl" />
      </div>
      <h2 className="mt-6 text-3xl xl:text-4xl font-bold text-center leading-tight tracking-tight" data-testid="live-player-name">{p.name}</h2>
      <div className="flex justify-center gap-2 mt-3 flex-wrap">
        <span className="px-3 py-1 rounded-full text-xs font-bold border border-[#FF5722]/40 text-[#FF8A65] bg-[#FF5722]/10">{p.role}</span>
        <span className="px-3 py-1 rounded-full text-xs font-bold border border-[#E5B842]/40 text-[#F5D061]">{p.category_name}</span>
        {p.is_sample && <span className="px-3 py-1 rounded-full text-xs font-bold border border-slate-500 text-slate-300">SAMPLE</span>}
      </div>
      <div className="mt-5 text-center">
        <p className="eyebrow">Base price</p>
        <p className="num text-2xl font-bold mt-1" data-testid="live-base-price">{fmtL(p.effective_base_price)}</p>
      </div>
      <div className="text-xs text-slate-400 text-center mt-2">{[p.batting_style, p.bowling_style].filter(Boolean).join(" · ")}</div>
      <div className="grid grid-cols-3 gap-2 mt-auto pt-5">
        {stats.map(([k, v]) => (
          <div key={k} className="bg-white/[0.04] border border-white/5 rounded-xl p-2 text-center">
            <div className="num font-bold">{v}</div>
            <div className="text-[9px] uppercase tracking-widest text-slate-500">{k.replace("_", " ")}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function SoldOverlay({ lastEvent, snap }) {
  const [show, setShow] = useState(null);
  useEffect(() => {
    if (lastEvent && ["sold", "unsold"].includes(lastEvent.event)) {
      setShow(lastEvent.event);
      const t = setTimeout(() => setShow(null), 3500);
      return () => clearTimeout(t);
    }
  }, [lastEvent]);
  if (!show || !snap?.player) return null;
  const team = snap.teams.find((t) => t.id === snap.state.leading_team_id);
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/70 backdrop-blur-sm pointer-events-none" data-testid="sold-overlay">
      <div className="text-center rise">
        <div className={`font-display font-extrabold text-[18vw] leading-none tracking-tight ${show === "sold" ? "shine gold-glow" : "text-slate-300"}`}>{show === "sold" ? "SOLD" : "UNSOLD"}</div>
        <div className="text-3xl font-bold mt-2">{snap.player.name}</div>
        {show === "sold" && <div className="text-2xl mt-2 text-[#F5D061]">{team?.name} · <span className="num">{fmtL(snap.state.current_bid)}</span></div>}
      </div>
    </div>
  );
}

export default function LiveDisplay() {
  const { clubId } = useParams();
  if (!clubId) return <ClubPicker base="/live" />;
  return <LiveBoard clubId={clubId} />;
}

function LiveBoard({ clubId }) {
  const [notes, setNotes] = useState([]);
  const [soundOn, setSoundOn] = useState(false);
  const { snap, connected, offset, lastEvent, wheel } = useAuction({ club: clubId, onNotification: (n) => !n.team_id && setNotes((x) => [n, ...x].slice(0, 6)) });
  useAuctionFx(lastEvent, soundOn);
  const [flash, setFlash] = useState(0);
  useEffect(() => { if (lastEvent?.event === "bid") setFlash(lastEvent.at); }, [lastEvent]);
  if (!snap) return <div className="min-h-screen grid place-items-center text-slate-400">Connecting to auction…</div>;
  const st = snap.state;
  const leader = snap.teams.find((t) => t.id === st.leading_team_id);
  const fs = () => document.documentElement.requestFullscreen?.();
  return (
    <div className="min-h-screen lg:h-screen flex flex-col stage-bg relative overflow-hidden grain" data-testid="live-display">
      <SoldOverlay lastEvent={lastEvent} snap={snap} />
      <SpinWheel wheel={wheel} soundOn={soundOn} />
      <div className="flex items-center justify-between px-6 py-3 border-b border-[#D4AF37]/20 bg-[#030712]/60 backdrop-blur-xl relative z-10">
        <Logo />
        <div className="hidden md:block text-center">
          <p className="text-sm uppercase tracking-[0.3em] font-semibold text-[#F5D061]">{snap.club?.name} auction · {snap.config?.title || "OPL Season 5 Player Auction"}</p>
          <p className="text-xs text-slate-500">Round {st.round} · Sold {snap.totals.sold} · Unsold {snap.totals.unsold} · Remaining {snap.totals.remaining}</p>
        </div>
        <div className="flex items-center gap-3">
          <Sponsors sponsors={snap.sponsors} className="hidden lg:flex mr-3" />
          <button onClick={() => setSoundOn(!soundOn)} className="p-2 rounded hover:bg-white/5" data-testid="sound-toggle-btn" title="Sound">{soundOn ? <Volume2 className="h-4 w-4 text-[#E5B842]" /> : <VolumeX className="h-4 w-4" />}</button>
          <span data-testid="ws-status" className={`flex items-center gap-1 text-xs ${connected ? "text-emerald-400" : "text-red-400"}`}>
            {connected ? <Wifi className="h-4 w-4" /> : <WifiOff className="h-4 w-4" />}{connected ? "LIVE SYNC" : "RECONNECTING"}
          </span>
          <button onClick={fs} className="p-2 rounded hover:bg-white/5" data-testid="fullscreen-btn"><Maximize className="h-4 w-4" /></button>
        </div>
      </div>
      <div className="flex-1 grid grid-cols-1 lg:grid-cols-12 gap-5 p-5 min-h-0">
        <div className="lg:col-span-3 min-h-0"><PlayerPanel p={snap.player} status={st.status} /></div>
        <div className="lg:col-span-5 flex flex-col gap-5 min-h-0">
          <div key={flash} className={`glass-navy gold-frame rounded-3xl p-8 text-center relative overflow-hidden ${flash ? "bid-flash" : ""}`}>
            <div className="absolute inset-x-0 -top-20 mx-auto h-48 w-2/3 rounded-full bg-[#D4AF37]/15 blur-3xl pointer-events-none" />
            <StatusPill status={st.status} big />
            <p className="eyebrow mt-6">Current bid</p>
            <div className="font-display font-extrabold text-6xl sm:text-7xl xl:text-8xl shine gold-glow mt-2 num tracking-tight relative" data-testid="current-bid-display">
              {st.status === "UNSOLD" ? <span className="text-6xl" style={{ WebkitTextFillColor: "#94A3B8" }}>UNSOLD</span> : st.current_bid != null ? fmtL(st.current_bid) : snap.player ? fmtL(snap.player.effective_base_price) : <span className="text-3xl tracking-[0.2em] font-semibold" style={{ WebkitTextFillColor: "#94A3B8", textShadow: "none" }}>AWAITING LOT</span>}
            </div>
            <div className="mt-4 flex items-center justify-center gap-3 min-h-[44px]" data-testid="highest-bidder">
              {leader ? <><TeamBadge team={leader} size={40} /><span className="text-2xl font-bold">{leader.name}</span></> :
                <span className="text-slate-500">{snap.player && st.status !== "UNSOLD" ? "Opening at base price" : ""}</span>}
            </div>
            <div className="mt-6 max-w-md mx-auto"><TimerBar snap={snap} offset={offset} big /></div>
            {st.status === "PAUSED" && st.message && <p className="mt-3 text-yellow-300 text-sm">{st.message}</p>}
          </div>
          <div className="glass-navy rounded-3xl p-4 flex-1 min-h-[160px] overflow-hidden">
            <p className="eyebrow mb-3">Bid battle</p>
            <div className="space-y-1.5" data-testid="live-bid-history">
              {snap.recent_bids.map((b, i) => (
                <div key={b.id} className={`flex justify-between items-center px-3 py-2 rounded-lg ${i === 0 ? "bg-[#E5B842]/10 border border-[#E5B842]/30 rise" : "bg-white/[0.02]"}`}>
                  <span className="font-semibold">{b.team_name}</span><span className="num font-bold">{fmtL(b.amount)}</span>
                </div>
              ))}
              {!snap.recent_bids.length && <p className="text-slate-500 text-sm">No bids yet</p>}
            </div>
          </div>
        </div>
        <div className="lg:col-span-4 glass-navy rounded-3xl p-4 overflow-y-auto min-h-0" data-testid="live-teams-board">
          <p className="eyebrow mb-3">Franchise purse board</p>
          <div className="space-y-2">
            {snap.teams.map((t) => {
              const lead = t.id === st.leading_team_id && !["SOLD", "UNSOLD"].includes(st.status);
              const pct = t.initial_purse ? (t.remaining_purse / t.initial_purse) * 100 : 0;
              return (
                <div key={t.id} data-testid={`team-purse-${t.code}`} className={`rounded-2xl p-3 border transition-[background-color,border-color,box-shadow] duration-300 ${lead ? "border-[#F5D061] bg-[#D4AF37]/15 shadow-[0_0_25px_rgba(245,208,97,.35)]" : "border-white/5 bg-white/[0.03]"}`}>
                  <div className="flex items-center gap-3">
                    <TeamBadge team={t} size={34} />
                    <div className="flex-1 min-w-0">
                      <div className="font-bold truncate text-sm">{t.name}</div>
                      <div className="text-[11px] text-slate-400">Squad {t.players_count}/{snap.config?.max_squad || "∞"}</div>
                    </div>
                    <div className="text-right">
                      <div className="num font-bold">{fmtL(t.remaining_purse)}</div>
                      {lead && <div className="text-[10px] font-bold text-[#E5B842] tracking-widest">LEADING</div>}
                    </div>
                  </div>
                  <div className="h-1.5 mt-2 rounded-full bg-white/5 overflow-hidden"><div className="h-full rounded-full gold-bg transition-[width] duration-500" style={{ width: `${pct}%` }} /></div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      <div className="border-t border-[#D4AF37]/25 bg-[#030712]/70 backdrop-blur-xl grid grid-cols-1 md:grid-cols-12 text-sm relative z-10">
        <div className="md:col-span-3 px-5 py-3 border-r border-white/5" data-testid="next-player-preview">
          <span className="eyebrow">Up next</span>
          <div className="font-bold truncate">{snap.next_player ? `${snap.next_player.name} · ${snap.next_player.role}` : "Picked by the wheel"}</div>
        </div>
        <div className="md:col-span-6 px-5 py-3 overflow-hidden whitespace-nowrap" data-testid="recent-sold-ticker">
          <span className="eyebrow mr-3">Recently sold</span>
          {snap.recent_sold.map((s) => <span key={s.id} className="mr-6">{s.player_name} → <b className="text-[#F5D061]">{s.team_name}</b> <span className="num">{fmtL(s.price)}</span></span>)}
          {!snap.recent_sold.length && <span className="text-slate-500">No sales yet</span>}
        </div>
        <div className="md:col-span-3 px-5 py-3 border-l border-white/5 truncate" data-testid="live-notifications">
          <span className="eyebrow mr-2">Feed</span>{notes[0]?.message || "Welcome to the OPL Season 6 auction"}
        </div>
      </div>
    </div>
  );
}
